import * as React from "react";

import {
  confirmResetCredentials,
  sendResetCredentialsRequest,
} from "@/api/api";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Spinner } from "./ui/spinner";
import { AuthFrame } from "./auth-frame";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { saveTokens } from "@/auth";

const requestFormSchema = z.object({
  email: z.string().nonempty({
    message: "You must input an email address!",
  }),
});
const resetFormSchema = z.object({
  password: z.string().nonempty({
    message: "You must input a new password.",
  }),
  confirmPassword: z.string().nonempty({
    message: "Please confirm your password.",
  }),
  token: z.string().nonempty(),
});
interface ResetCredentialsProps extends React.HTMLAttributes<HTMLDivElement> { }

export function ResetCredentialsRequest({
  className,
}: ResetCredentialsProps) {
  const [successMessage, setSuccessMessage] = React.useState("");
  const form = useForm<z.infer<typeof requestFormSchema>>({
    resolver: zodResolver(requestFormSchema),
    defaultValues: {
      email: "",
    },
  });

  const resetRequestMutation = useMutation({
    mutationFn: async (credentials: any) => {
      const res = await sendResetCredentialsRequest(credentials);
      if (res?.error) {
        setSuccessMessage("");
        form.setError("root", { type: "server", message: res.error });
      } else {
        setSuccessMessage(
          "Please check your email for instructions on resetting your password."
        );
      }
    },
  }) as any;

  function onSubmit(values: z.infer<typeof requestFormSchema>) {
    resetRequestMutation.mutate(values);
  }

  return (
    <AuthFrame>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <div className="grid gap-6">
            <div className="grid gap-2">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input placeholder="Email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <Button
              type="submit"
              disabled={
                !form.formState.isValid || resetRequestMutation.isLoading
              }
            >
              {resetRequestMutation.isLoading ? (
                <Spinner className="h-6 w-6" />
              ) : (
                "Reset Password"
              )}
            </Button>
            {successMessage !== "" ? (
              <p
                className={cn("text-sm font-medium text-green-500", className)}
              >
                {successMessage}
              </p>
            ) : (
              <p
                className={cn(
                  "text-sm font-medium text-destructive",
                  className
                )}
              >
                {form.formState.errors.root?.message}
              </p>
            )}
          </div>
        </form>
      </Form>
    </AuthFrame>
  );
}

export function ResetCredentials({
  className,
}: ResetCredentialsProps) {
  const [successMessage, setSuccessMessage] = React.useState("");
  const location = useLocation();
  const token = new URLSearchParams(location.search).get("token");
  const navigate = useNavigate();
  const form = useForm<z.infer<typeof resetFormSchema>>({
    resolver: zodResolver(resetFormSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
      token: token ? token : undefined,
    },
  });

  const resetMutation = useMutation({
    mutationFn: async (credentials: any) => {
      const res = await confirmResetCredentials({ ...credentials, token });
      if (res?.error) {
        form.setError("root", { type: "server", message: res.error });
      } else if (res?.access && res?.refresh) {
        // Save the JWT tokens for auto-login
        saveTokens({
          access: res.access,
          refresh: res.refresh,
        });
        setSuccessMessage(
          "Password reset successfully! Logging you in..."
        );
        // Redirect to organization page (main dashboard)
        setTimeout(() => {
          window.location.href = "/organization";
        }, 1500);
      } else {
        // Fallback if tokens not received
        setSuccessMessage(
          "Password reset successfully! Please log in."
        );
        setTimeout(() => {
          navigate({ to: "/login" });
        }, 2000);
      }
    },
  });

  function onSubmit(values: z.infer<typeof resetFormSchema>) {
    if (values.password !== values.confirmPassword) {
      form.setError("root", {
        type: "passwordCheck",
        message: "Passwords must match!",
      });
    } else {
      resetMutation.mutate(values);
    }
  }

  return (
    <AuthFrame>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <div className="grid gap-6">
            <div className="grid gap-2">
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder="Password"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder="Confirm Password"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <Button type="submit">Reset Password</Button>
            {successMessage !== "" ? (
              <p
                className={cn("text-sm font-medium text-green-500", className)}
              >
                {successMessage}
              </p>
            ) : (
              <p
                className={cn(
                  "text-sm font-medium text-destructive",
                  className
                )}
              >
                {form.formState.errors.root?.message}
              </p>
            )}
          </div>
        </form>
      </Form>
    </AuthFrame>
  );
}
